import { useEffect, useState } from 'react';
import { Typography, Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { useTranslation } from 'react-i18next';
import { CustomAvatar } from '@/components/avatar/Avatar';
import { InfoBox } from '@/components/infoBox/InfoBox';
import { PageBase } from '@/pages/PageBase';
import { theme } from '@/styles/theme';
import Button from '@/components/button/Button';
import WarningField from '@/components/warningField/WarningField';
import { useAuthStore } from '@/stores/AuthStore';
import { capitalizeFirstLetterOfEachWord } from '@/utils/getInitials';
import { getLocale } from '@/utils/formatedLanguage';
import {
  format,
  subMinutes,
  isBefore,
  isAfter,
  differenceInHours,
  addMinutes,
} from 'date-fns';
import { useAppointmentsService } from '@/data/services/useAppointmentsService';
import { AppError } from '@/utils/AppError';
import { renderSkeleton } from './components/RenderSkeleton';
import CustomPopup from '@/components/customPopup/CustomPopup';
import { useSnackbarStoreComponent } from '@/stores/useSnackbar';
import { dateFormatter } from '@/utils/dateFormatter';
import { useAppointmentTypeService } from '@/data/services/schedule/useLocationService';
import { AppointmentTypeDTO } from '@/data/DTOs/schedule/AppointmentTypeDTO';

enum AppointmentType {
  IN_PERSON = '1',
  ONLINE = '2',
}

export const AppointmentDetails = () => {
  const { t } = useTranslation([
    'appointmentDetails',
    'schedule',
    'creditCard',
  ]);
  const { getAppointmentDetails, cancelAppointment } = useAppointmentsService();
  const { getAppointmentTypeList } = useAppointmentTypeService();
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  const [appointmentsType, setAppointmentsType] = useState<
    AppointmentTypeDTO[]
  >([]);
  const [showWarning, setShowWarning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [showReschedulingPopup, setShowReschedulingPopup] = useState(false);
  const [details, setDetails] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthStore();
  const { showSnackbar } = useSnackbarStoreComponent();
  const userFullName = `${user?.name || ''} ${user?.surname || ''}`;
  const locale = getLocale();
   const { formatToDateGMT, getUserLocalTimeOrClinicTime } = dateFormatter();

  

  const handleWarningRightButtonAction = () => {
    navigate(-1);
  };

  const handleCancelAppointment = () => {
    if (!appointmentId) return;
    setShowCancelPopup(false);
    setShowReschedulingPopup(false);
    setLoading(true);

    cancelAppointment(appointmentId)
      .then(() => {
        navigate('/');
        showSnackbar(t('consultation_cancelled'), 'success');
      })
      .catch((error: AppError) => {
        showSnackbar(t(error.message), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const appointmentDateTime: Date | null =
  details?.date && details?.time && typeof details.location?.gmt === 'number'
    ? formatToDateGMT(details.date, details.time, details.location.gmt)
    : null;
    const currentDate = new Date();

    const diffHours = appointmentDateTime
      ? differenceInHours(appointmentDateTime, currentDate)
      : 0;
    
    const canCancel = appointmentDateTime
      ? isBefore(currentDate, appointmentDateTime)
      : false;
    
    const canReschedule = diffHours > 3;
    
    const hasAppointmentPassed = appointmentDateTime
      ? isAfter(currentDate, appointmentDateTime)
      : false;
    
    const fifteenMinutesBefore: Date | null = appointmentDateTime
      ? subMinutes(appointmentDateTime, 15)
      : null;
    
    const twentyMinutesAfter: Date | null = appointmentDateTime
      ? addMinutes(appointmentDateTime, 20)
      : null;
    
    const isAccessible: boolean =
      appointmentDateTime && fifteenMinutesBefore && twentyMinutesAfter
        ? isAfter(currentDate, fifteenMinutesBefore) &&
          isBefore(currentDate, twentyMinutesAfter)
        : false;
    

  useEffect(() => {
    if (appointmentId) {
      getAppointmentDetails(appointmentId)
        .then((data) => {
          console.log('Detalhes do agendamento:', data);

          setDetails(data);
        })
        .catch((err: AppError) => {
          setError(t(err.message));
          setShowWarning(true);
        });
    } else {
      setError('appointmentId do agendamento não fornecido.');
    }
  }, [appointmentId, getAppointmentDetails]);

  useEffect(() => {
    getAppointmentTypeList()
      .then((data) => {
        setAppointmentsType(data);
      })
      .catch((err: AppError) => {
        setError(t(err.message));
        setShowWarning(true);
      });
  }, []);

  if (error) {
    return (
      <PageBase title="">
        <WarningField
          description={error}
          isHidden={!showWarning}
          setShowWarning={setShowWarning}
          status="warning"
          rightButtonTitle={t('back_to_home_btn')}
          rightButtonAction={handleWarningRightButtonAction}
        />
      </PageBase>
    );
  }

  if (!details) {
    return (
      <PageBase
        title={t('page_title')}
        onBackClick={() => navigate(-1)}
        paddingInline={1}
      >
        {renderSkeleton()}
      </PageBase>
    );
  }

  const localTime = getUserLocalTimeOrClinicTime(
    details.date,
    details.time,
    details.location.gmt
  );
  
  const dateFormatted = details?.date
    ? format(localTime.asDate, 'PPP', { locale })
    : 'Data não informada';
  
  const timeFormatted = details?.time
    ? format(localTime.asDate, 'hh:mm a', { locale })  // ou 'hh:mm a' se preferir AM/PM
    : 'Hora não informada';
  

  //const timeDifference = calculateTimeDifference(details.location.gmt);

  const patientFullName = details?.dependent
    ? capitalizeFirstLetterOfEachWord(
        `${details.dependent.name || ''} ${
          details.dependent.surname || ''
        }`.trim()
      )
    : capitalizeFirstLetterOfEachWord(userFullName || '');

  return (
    <PageBase
      title={t('page_title')}
      onBackClick={() => navigate(-1)}
      paddingInline={1}
    >
      <Box>
        <Typography
          sx={{ fontSize: '1.8rem', color: theme.palette.primary.main }}
        >
          {t('appoitment_title')}
        </Typography>
        <Typography sx={styles.paymentTitleStyle}>
          {t('appoitment_description')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: { xs: '90vw', sm: '100%' },
        }}
      >
        <Box sx={styles.header}>
          <CustomAvatar
            name={patientFullName}
            src={
              details?.dependent
                ? details?.dependent?.profilePhotoUrl
                  ? details?.dependent?.profilePhotoUrl
                  : ''
                : user?.picture
                ? user.picture
                : ''
            }
          />
          <Box>
            <Typography sx={styles.headerTitle}>{t('patient_name')}</Typography>
            <Typography sx={styles.headerName}>
              {capitalizeFirstLetterOfEachWord(patientFullName)}
            </Typography>
          </Box>
        </Box>
        <Box sx={styles.borderBase}>
          <InfoBox
            iconLeft={<LocalHospitalOutlinedIcon sx={styles.iconLeft} />}
            title={t('type_attendance')}
            text={
              details.appointmentTypeId === AppointmentType.ONLINE
                ? t('online_consultation')
                : `${t('in_person')} | ${
                    details.location?.name || t('address_not_provided')
                  } ${details.location?.addressLine2 || ''}`
            }
          />
          <Button
            id="appointment-button"
            disable={
              details.appointmentTypeId === AppointmentType.ONLINE
                ? !isAccessible
                : false
            }
            title={
              details.appointmentTypeId === AppointmentType.ONLINE
                ? t('online_consultation_available')
                : t('view_address')
            }
            sx={{
              ...styles.borderBase,
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              padding: 1,
              display: 'flex',
              justifyContent: 'center',
              borderTopRightRadius: 0,
              borderTopLeftRadius: 0,
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => {
              if (details.appointmentTypeId !== AppointmentType.ONLINE) {
                const address = details.location?.addressLine1 || '';

                window.open(
                  `https://maps.google.com/?q=${encodeURIComponent(address)}`,
                  '_blank'
                );
              } else {
                window.open(details.appointmentLink, '_blank');
              }
            }}
          />
        </Box>
        <Box sx={styles.borderBase}>
          <InfoBox
            iconLeft={<MedicalServicesOutlinedIcon sx={styles.iconLeft} />}
            title={t('doctor_name')}
            text={`Dr(a) ${details.doctor?.firstName || ''} ${
              details.doctor?.middleName || ''
            } ${details.doctor?.lastName || ''}`}
          />
        </Box>
        <Box sx={styles.borderBase}>
          <InfoBox
            iconLeft={<EventOutlinedIcon sx={styles.iconLeft} />}
            title={t('date_and_time')}
            text={dateFormatted}
            subText={`às ${timeFormatted} ${localTime.timeZone}`}
          />
        </Box>
        <Box sx={styles.borderBase}>
          <InfoBox
            iconLeft={<MonetizationOnOutlinedIcon sx={styles.iconLeft} />}
            title={t('payment_method')}
            text={
              details.cardLast4Digits
                ? t('creditCard:screen_credit_card_title')
                : t('local_payment')
            }
            subText={`${
              details.cardLast4Digits
                ? `${details.banner} **** ${details.cardLast4Digits}`
                : ''
            }`}
          />
        </Box>
      </Box>

      <Box>
        <Typography sx={styles.paymentTitleStyle}>
          {t('payment_title')}
        </Typography>
      </Box>
      <Box sx={styles.borderBase}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', padding: 2 }}
        >
          <Typography sx={styles.paymentValueTitle}>
            {t('total_value')}
          </Typography>
          <Typography sx={styles.paymentValue}>
            U$
            {appointmentsType
              .filter((item) => item.id === details.appointmentTypeId)[0]
              ?.price?.toFixed(2) || ''}
          </Typography>
        </Box>
      </Box>

      {hasAppointmentPassed && canCancel && canReschedule && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: '#FFF8E1',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#FFA000' }} />
          <Typography sx={{ color: '#FFA000', fontSize: '1.4rem' }}>
            {t('cannot_cancel')}
          </Typography>
        </Box>
      )}

      <Box sx={styles.buttonsContainer}>
        <Button
          title={t('reschedule_appointment')}
          outlined
          disable={!canReschedule || loading}
          onClick={() => setShowReschedulingPopup(true)}
        />
        <Button
          title={t('cancel_appointment')}
          disable={!canCancel || loading}
          loading={loading}
          outlined
          onClick={() => setShowCancelPopup(true)}
        />
      </Box>

      <CustomPopup
        title={t('cancel_popup_title')}
        description={t('cancel_popup_description')}
        popupHidden={!showCancelPopup}
        imageHidden={true}
        closeHidden={false}
        showObservationMessage={true}
        closeAction={() => setShowCancelPopup(false)}
        buttonTitleArray={[t('cancel'), t('confirm')]}
        buttonOnlyTextArray={[true, false]}
        buttonOutlinedArray={[true, false]}
        buttonColorArray={['inherit', 'error']}
        buttonActionArray={[
          () => setShowCancelPopup(false),
          handleCancelAppointment,
        ]}
      />

      <CustomPopup
        title={t('schedule:re_schedule_popup_title')}
        description={t('schedule:re_schedule_popup_description')}
        popupHidden={!showReschedulingPopup}
        imageHidden={true}
        closeHidden={false}
        closeAction={() => setShowReschedulingPopup(false)}
        buttonTitleArray={[t('confirm'), t('cancel')]}
        buttonOutlinedArray={[true, true]}
        buttonOnlyTextArray={[false, true]}
        buttonColorArray={['error', 'primary']}
        buttonActionArray={[
          () =>
            navigate(`/rescheduling/${appointmentId}`, {
              state: {
                appointmentEdit: {
                  ...details,
                  userDependentId: details.dependent?.id,
                },
                userFullName,
              },
            }),
          () => setShowReschedulingPopup(false),
        ]}
      />
    </PageBase>
  );
};

const styles = {
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 2,
    mt: 4,
  },
  header: {
    padding: 2,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  headerTitle: {
    color: '#4f4f4f',
    fontSize: '1.2rem',
    fontWeight: 600,
  },
  headerName: {
    color: '#000',
    fontSize: '1.4rem',
  },
  paymentValueTitle: {
    color: '#4f4f4f',
    fontSize: '1.8rem',
  },
  paymentValue: {
    color: '#000',
    fontSize: '1.8rem',
    fontWeight: 500,
  },
  iconLeft: {
    fontSize: '2rem',
  },
  borderBase: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
  },
  paymentTitleStyle: {
    fontSize: '1.4rem',
    fontWeight: 600,
    paddingTop: 2,
    paddingBottom: 2,
  },
};
